/**
 * Rule-based path group card for Binom: header + draggable list of rule paths.
 * Drag handles reorder within the group; "+ Path" appends a new path.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { PATH_LIST_CONTAINER_CLASS } from '@/features/flow-optimizer/tabs/standard/v2/paths/path-list-chrome';
import {
  usePreviewMode,
  usePreviewRowLookup,
} from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import {
  PreviewSortLabel,
  usePreviewMetricSort,
} from '@/features/flow-optimizer/components/preview/sort';

import type {
  BinomDraftPath,
  BinomDraftPathGroup,
} from '../../binom-flow-draft.types';

import BinomDraggablePathRow from './BinomDraggablePathRow';
import {
  groupPathSelection,
  isGroupPathSelected,
  type BinomPathsSelection,
} from '../binom-paths-selection';
import { pathSharePercents } from './path-share-percent';
import { dragEndToMove, useBinomDragSensors } from './use-binom-drag-sensors';

export interface BinomRuleGroupCardProps {
  group: BinomDraftPathGroup;
  groupIndex: number;
  selection: BinomPathsSelection;
  onSelect: (next: BinomPathsSelection) => void;
  onPatchPath: (pathIdx: number, m: (p: BinomDraftPath) => void) => void;
  onAddPath: () => void;
  onMovePath: (from: number, to: number) => void;
}

const BinomRuleGroupCard: FC<BinomRuleGroupCardProps> = ({
  group,
  groupIndex,
  selection,
  onSelect,
  onPatchPath,
  onAddPath,
  onMovePath,
}) => {
  const previewMode = usePreviewMode();
  const lookup = usePreviewRowLookup();
  const { sort, cycle, sortEntries } = usePreviewMetricSort();
  const percents = pathSharePercents(group.paths);
  const sensors = useBinomDragSensors();
  const ids = group.paths.map((p) => p.id);
  const pathEntries = useMemo(() => {
    const base = group.paths.map((path, index) => ({ path, index }));
    if (!previewMode) return base;
    return sortEntries(base, ({ path }) => ({
      active: path.active !== false,
      name: path.name ?? '',
      row: lookup('paths', path.id),
    }));
  }, [group.paths, previewMode, sortEntries, lookup]);

  const renderPaths = () =>
    pathEntries.map(({ path, index: pathIdx }) => (
      <BinomDraggablePathRow
        key={path.id}
        id={path.id}
        path={path}
        weightPercent={percents[pathIdx] ?? 0}
        selected={isGroupPathSelected(selection, groupIndex, pathIdx)}
        onSelect={() => onSelect(groupPathSelection(groupIndex, pathIdx))}
        onPatch={(m) => onPatchPath(pathIdx, m)}
        dragDisabled={previewMode}
      />
    ));

  return (
    <div className="space-y-1 rounded-md border border-border/60 bg-muted/10 p-1.5">
      <div className="flex items-baseline justify-between gap-2 px-1">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
          {group.name?.trim() ? group.name : 'Rule group'}
        </h4>
        <div className="flex items-center gap-2">
          <span className="text-[10px] tabular-nums text-muted-foreground">
            {group.paths.length}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-1.5 text-[10px]"
            onClick={onAddPath}
            aria-label={`Add path to ${group.name}`}
          >
            <Plus className="h-3 w-3" aria-hidden /> Path
          </Button>
        </div>
      </div>
      {group.paths.length === 0 ? (
        <p className="px-1 text-[11px] italic text-muted-foreground">
          No paths in this rule group.
        </p>
      ) : previewMode ? (
        <>
          <div className="flex items-stretch">
            <span className="w-4 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1 px-1.5">
              <div className="grid w-full items-center gap-x-1.5 grid-cols-[minmax(0,1fr)_3rem_0.75rem_3rem_3.25rem_2.75rem]">
                <span />
                <PreviewSortLabel column="current" label="Current" sort={sort} onCycle={cycle} />
                <span />
                <PreviewSortLabel column="new" label="New" sort={sort} onCycle={cycle} />
                <PreviewSortLabel column="diff" label="Diff" sort={sort} onCycle={cycle} />
                <span />
              </div>
            </div>
          </div>
          <div
            className={PATH_LIST_CONTAINER_CLASS}
            role="listbox"
            aria-label={`${group.name} paths`}
          >
            {renderPaths()}
          </div>
        </>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => {
            const m = dragEndToMove(e, ids);
            if (m) onMovePath(m.from, m.to);
          }}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div
              className={PATH_LIST_CONTAINER_CLASS}
              role="listbox"
              aria-label={`${group.name} paths`}
            >
              {renderPaths()}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default BinomRuleGroupCard;
